import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import AlertMessage from 'components/UI/V3/AlertMessage';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { FunctionComponent, KeyboardEvent, ClipboardEvent } from 'react';
import styles from './TaxOnSurcharge.module.scss';

interface TaxOnSurchargeProps {
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
}

const TAX_RATE_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;

const isValidTaxRateInput = (value: string): boolean => {
    if (value === '') {
        return true;
    }

    if (!TAX_RATE_PATTERN.test(value)) {
        return false;
    }

    const numericValue = Number(value.endsWith('.') ? value.slice(0, -1) : value);

    return !Number.isNaN(numericValue) && numericValue <= 100;
};

const getNextInputValue = (input: HTMLInputElement, inserted: string): string => {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;

    return `${input.value.slice(0, start)}${inserted}${input.value.slice(end)}`;
};

export const TaxOnSurcharge: FunctionComponent<TaxOnSurchargeProps> = ({ value, disabled = false, onChange }) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }

        if (event.key.length !== 1) {
            return;
        }

        const insertedKey = event.key === ',' ? '.' : event.key;
        const nextValue = getNextInputValue(event.currentTarget, insertedKey);

        if (event.key === ',') {
            event.preventDefault();
            if (isValidTaxRateInput(nextValue)) {
                document.execCommand('insertText', false, '.');
            }
            return;
        }

        if (!isValidTaxRateInput(nextValue)) {
            event.preventDefault();
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        const pastedValue = event.clipboardData.getData('text').replace(',', '.');
        const nextValue = getNextInputValue(event.currentTarget, pastedValue);

        if (!isValidTaxRateInput(nextValue)) {
            event.preventDefault();
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.field}>
                <Label htmlFor="business_settings_payment_processing_input_tax_on_surcharge">Tax on Surcharge</Label>
                <div className={styles.input}>
                    <Input
                        id="business_settings_payment_processing_input_tax_on_surcharge"
                        name="taxOnSurcharge"
                        value={value}
                        placeholder="0"
                        autoFocus={false}
                        inputMode="decimal"
                        disabled={disabled}
                        endIcon={<span className={styles.percentageSymbol}>%</span>}
                        onChange={event => onChange(event.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                    />
                </div>
            </div>

            <AlertMessage
                icon={faInfoCircle}
                iconColor="#BAA997"
                bdColor="#BCB8AE"
                bgColor="#F8F5F1"
            >
                <div className={styles.alertText}>
                    <p>
                        <strong>Enter the tax rate</strong> your business applies to the surcharge. This tax is
                        calculated only on the surcharge amount, not on the base service price.
                    </p>
                    <p>
                        <strong>Important:</strong> Your business is responsible for configuring the appropriate rate
                        based on applicable tax requirements. This setting does not use or change taxes configured for
                        services or invoice items.
                    </p>
                </div>
            </AlertMessage>
        </div>
    );
};

export default TaxOnSurcharge;
